/* eslint-disable import/no-named-as-default */
import React, { PureComponent } from 'react';
import { Redirect, RouteComponentProps, Route, Switch, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import { NavBar, NavBarList, NavBarListItem } from '@spotify-internal/encore-creator-web';
import { ConnectedPodcastAudience } from '../PodcastAudience';
import {
  LOADING,
  ERROR,
  UNAUTHORIZED,
  selectPodcastMetadata,
  selectPodcastMetadataStatus,
  selectDatesAvailable,
  selectMe,
} from '../../../store/selectors';
import { updateDateRange } from '../../../store/updateDateRange';
import { loadPodcastMetadata } from '../../../store/podcastMetadata';
import { trackEvent } from '../../../lib/Tracking';

import { ConnectedEpisodeList } from '../../../components/Episode/EpisodeList';
import { Header } from '../../Shared/Header';
import { Helmet } from 'react-helmet';
import { DataRange, Me, ReduxState, Trailer, Show } from '../../../types';
import { PodcastDetails } from '../PodcastDetails';
import { TrailerSelectionModal } from '../TrailerSelectionModal';
import { injectIntl, IntlShape, defineMessage } from 'react-intl';

type Props = {
  status?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  me: Me;
  metadata?: Show;
  podcastId: string;
  loadPodcastMetadata(id: string): void;
  updateDateRange(startDate: string, endDate: string, label: string): void;
  datesAvailable?: DataRange;
  intl: IntlShape;
} & RouteComponentProps;

type State = {
  showTrailerSelectionOverlay: boolean;
  trailer?: Trailer | null;
};

export class InternationalizedPodcastLandingPage extends PureComponent<Props, State> {
  state = {
    showTrailerSelectionOverlay: false,
    trailer: undefined,
  };

  UNSAFE_componentWillMount() {
    const endDate = this.props.datesAvailable!.until;
    this.props.updateDateRange(
      moment(endDate)
        .subtract(6, 'days')
        .format('YYYY-MM-DD'),
      moment(endDate).format('YYYY-MM-DD'),
      'Last 7 days',
    );
  }

  componentDidMount() {
    trackEvent({ category: 'page-type', action: 'view', label: 'podcast-overview' });
    this.props.loadPodcastMetadata(this.props.podcastId);
  }

  componentDidUpdate(prevProps) {
    if (this.props.metadata !== prevProps.metadata) {
      this.setState({ trailer: this.props.metadata!.trailer });
    }
  }

  updateSelectedTrailer = (trailer: Trailer | null) => {
    this.setState({ trailer });
  };

  toggleTrailerSelectionModal = () => {
    this.setState(state => ({
      showTrailerSelectionOverlay: !state.showTrailerSelectionOverlay,
    }));
  };

  get navigationTabs() {
    const { intl } = this.props;
    return (
      <NavBar
        list={
          <NavBarList>
            <NavBarListItem
              label={intl.formatMessage(
                defineMessage({ id: 'podcast.tabs.episode', defaultMessage: 'Episodes' }),
              )}
              to="overview"
              active={this.getTabActiveState('overview')}
              onClick={() => this.handleTabChange('overview')}
              component={NavLink}
            />
            <NavBarListItem
              label={intl.formatMessage(
                defineMessage({ id: 'podcast.tabs.audience', defaultMessage: 'Audience' }),
              )}
              to="audience"
              active={this.getTabActiveState('audience')}
              onClick={() => this.handleTabChange('audience')}
              component={NavLink}
            />
            <NavBarListItem
              label={intl.formatMessage(
                defineMessage({ id: 'podcast.tabs.details', defaultMessage: 'Details' }),
              )}
              to="details"
              active={this.getTabActiveState('details')}
              onClick={() => this.handleTabChange('details')}
              component={NavLink}
            />
          </NavBarList>
        }
      ></NavBar>
    );
  }

  get headerProps() {
    const { metadata, me } = this.props;
    /* istanbul ignore next */
    if (!metadata) {
      return;
    }

    return {
      artworkUrl: metadata.artworkUrl,
      type: 'podcast',
      title: metadata.name,
      publisher: metadata.publisher,
      metrics: {
        starts: metadata.starts,
        streams: metadata.streams,
        listeners: metadata.listeners,
        followers: metadata.followers,
      },
      metadata: {
        numEpisodes: metadata.totalEpisodes,
      },
      navigation: {
        network: !me!.permissions.network ? {} : metadata.network,
      },
      toggleTrailerSelectionModal: this.toggleTrailerSelectionModal,
      trailer: this.state.trailer,
    };
  }

  getTabActiveState(tabName: string) {
    return window.location.pathname.includes(tabName);
  }

  // eslint-disable-next-line class-methods-use-this
  handleTabChange(index: string) {
    trackEvent({ category: 'page-type', action: 'view', label: `podcast-${index}` });
  }

  render() {
    const { metadata, status } = this.props;

    switch (status) {
      case LOADING:
        return <span data-qa="podcast-overview-loading" />;
      case ERROR:
        return <span data-qa="podcast-overview-error" />;
      case UNAUTHORIZED:
        return <Redirect to="/no-access" />;
      default:
        break;
    }
    if (!metadata) return null;
    const { podcastId, location } = this.props;

    return (
      <div data-qa="podcast-landing-page-container">
        <Header {...this.headerProps} />
        {this.navigationTabs}

        {this.state.showTrailerSelectionOverlay && (
          <TrailerSelectionModal
            toggleTrailerSelectionModal={this.toggleTrailerSelectionModal}
            updateSelectedTrailer={this.updateSelectedTrailer}
            trailer={this.state.trailer}
            metadata={metadata}
            podcastId={podcastId}
          />
        )}

        <Switch>
          {shouldRedirectOldRoutes(location.hash) && (
            <RedirectOldRoutes hash={location.hash} podcastId={this.props.podcastId} />
          )}
          <Route path="/podcast/:podcastId/audience">
            <>
              <Helmet title={`Audience - ${metadata.name}`} />
              <ConnectedPodcastAudience
                data-qa="podcast-audience-container"
                podcastId={podcastId}
              />
            </>
          </Route>
          <Route path="/podcast/:podcastId/details">
            <>
              <Helmet title={`Details - ${metadata.name}`} />
              <PodcastDetails
                data-testid="podcast-details"
                licensor={metadata.network.name}
                networkId={metadata.network.id}
                feedUrl={metadata.feedUrl}
                podcastId={this.props.podcastId}
                hasNetworkPermissions={this.props.me!.permissions.network}
                toggleTrailerSelectionModal={this.toggleTrailerSelectionModal}
                updateSelectedTrailer={this.updateSelectedTrailer}
                trailer={this.state.trailer}
                isTaxiFeed={metadata.taxi}
              />
            </>
          </Route>
          <Route
            path="/podcast/:podcastId/overview"
            render={routeProps => (
              <>
                <Helmet title={`Episodes - ${metadata.name}`} />
                <ConnectedEpisodeList
                  data-qa="episode-list"
                  podcastId={podcastId}
                  {...routeProps}
                />
              </>
            )}
          ></Route>
          <Redirect from="*" to={`/podcast/${podcastId}/overview`} />
        </Switch>
      </div>
    );
  }
}

function RedirectOldRoutes(props: { hash: string; podcastId: string }) {
  if (['#share', '#settings'].includes(props.hash)) {
    return <Redirect to={`/podcast/${props.podcastId}/details`} />;
  }

  return <Redirect to={`/podcast/${props.podcastId}/${props.hash.slice(1)}`} />;
}

function shouldRedirectOldRoutes(hash: string) {
  return ['#overview', '#audience', '#share', '#settings'].includes(hash);
}

function mapStateToProps(state: ReduxState) {
  return {
    status: selectPodcastMetadataStatus(state),
    metadata: selectPodcastMetadata(state),
    datesAvailable: selectDatesAvailable(state),
    me: selectMe(state)!,
  };
}

const actions = { loadPodcastMetadata, updateDateRange };

const PodcastLandingPage = injectIntl(InternationalizedPodcastLandingPage);
export const PodcastLandingPageWithRouter = connect(mapStateToProps, actions)(PodcastLandingPage);
